import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

import Seo from "@/components/Seo";
import { CoveButton } from "@/components/CoveButton";
import { Reveal, MaskedWords, Marquee, FloatingBlob, Pop } from "@/components/motion";
import {
  SITE, IMAGES, MANIFESTO, REASONS, DIFFERENCE, STEPS,
  TESTIMONIALS, MARQUEE, FAQS, LOGO,
} from "@/data/content";

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": ["HealthAndBeautyBusiness", "MedicalBusiness"],
  "@id": `${SITE.url}#business`,
  name: "Cranio Cove",
  legalName: "Cranio Cove",
  alternateName: "Cranio Cove Craniosacral Therapy",
  description:
    "Private craniosacral therapy practice with Laura McLelland, offering gentle, trauma-aware, individualized sessions for nervous-system regulation, stress relief, and deep restoration in Tarrytown, NY.",
  url: SITE.url,
  image: [IMAGES.heroWater, IMAGES.lauraPortrait, IMAGES.coveMoody],
  logo: LOGO,
  telephone: SITE.phoneHref,
  email: SITE.email,
  priceRange: "$$",
  currenciesAccepted: "USD",
  paymentAccepted: SITE.payments.join(", "),
  address: {
    "@type": "PostalAddress",
    streetAddress: "200 S. Broadway, Building 1, Suite 100",
    addressLocality: "Tarrytown",
    addressRegion: "NY",
    postalCode: "10591",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 41.0762,
    longitude: -73.8587,
  },
  areaServed: [
    { "@type": "City", name: "Tarrytown" },
    { "@type": "AdministrativeArea", name: "Westchester County, NY" },
    { "@type": "Place", name: SITE.serviceArea },
  ],
  founder: {
    "@type": "Person",
    name: "Laura McLelland",
    jobTitle: "Occupational Therapist & Craniosacral Practitioner",
  },
  employee: {
    "@type": "Person",
    name: "Laura McLelland",
    jobTitle: "Occupational Therapist & Craniosacral Practitioner",
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Wednesday",
      opens: "10:00",
      closes: "18:00",
    },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Craniosacral Sessions",
    itemListElement: [
      { "@type": "Offer", name: "Initial Cranio Session (90 minutes)", url: `${SITE.url}/services` },
      { "@type": "Offer", name: "1-Hour Cranio Session", url: `${SITE.url}/services` },
      { "@type": "Offer", name: "30-Minute Mini Session", url: `${SITE.url}/services` },
    ],
  },
  makesOffer: {
    "@type": "Offer",
    itemOffered: {
      "@type": "Service",
      serviceType: "Craniosacral Therapy",
      name: "Craniosacral Therapy",
    },
  },
  knowsAbout: [
    "Craniosacral Therapy",
    "Nervous System Regulation",
    "Trauma-Informed Care",
    "Occupational Therapy",
    "Somatic Wellbeing",
  ],
  sameAs: [SITE.hearthUrl],
  slogan: SITE.heroTagline,
};

const Hero = () => (
  <section
    className="relative bg-gradient-to-br from-cove-petal via-cove-blossom/70 to-cove-blush/50 overflow-hidden"
    data-testid="hero-section"
  >
    <h1 className="sr-only">
      Cranio Cove, Craniosacral Therapy in {SITE.cityRegion}. Gentle bodywork to soothe your
      nervous system and release fascial tension. Deeply relaxing, Naturally restoring.
    </h1>

    <div className="relative w-full min-h-[92vh] md:min-h-[86vh] grid md:grid-cols-2">
      {/* LEFT, soft blush panel with logo + tagline + button */}
      <div className="relative flex flex-col items-center justify-center text-center px-8 md:px-14 py-16 md:py-20">
        <Reveal>
          <img
            src={LOGO}
            alt="Cranio Cove"
            className="w-72 sm:w-80 md:w-[26rem] lg:w-[30rem] xl:w-[34rem] object-contain mx-auto"
          />
        </Reveal>

        <Reveal delay={0.25}>
          <p className="mt-8 font-serif font-light text-[2rem] md:text-4xl lg:text-[2.9rem] leading-[1.08] text-cove-slate max-w-[18ch] mx-auto">
            Gentle bodywork to soothe your nervous system and release fascial tension.
          </p>
          <p className="mt-5 font-serif italic text-4xl sm:text-5xl md:text-5xl lg:text-6xl leading-[1.05] text-cove-duskyrose text-balance">
            Deeply relaxing, Naturally restoring.
          </p>
        </Reveal>

        <Reveal delay={0.4}>
          <div className="mt-10 hidden md:block">
            <Link
              to="/contact"
              data-testid="hero-book-button"
              className="inline-flex items-center rounded-full bg-cove-slate px-10 py-4 text-sm tracking-[0.28em] uppercase text-cove-cream hover:bg-cove-nightblue transition-colors duration-300 shadow-[0_18px_40px_-15px_rgba(41,54,80,0.55)]"
              aria-label="Book Your Session"
            >
              Book Your Session
            </Link>
          </div>
        </Reveal>
      </div>

      {/* RIGHT, arch-cropped sunset cove photo */}
      <div className="relative flex items-stretch">
        {/* Gold arch decoration curves (top-left of arch) */}
        <svg
          className="absolute -left-2 -top-2 md:-left-4 md:-top-4 w-[92%] h-[85%] pointer-events-none z-10"
          viewBox="0 0 400 500"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M20 480 C 20 220, 130 40, 380 20"
            stroke="#C79A4B"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.75"
          />
          <path
            d="M6 480 C 6 210, 120 24, 380 6"
            stroke="#C79A4B"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.45"
          />
        </svg>

        {/* Sparkles top-right */}
        <div className="absolute right-6 top-14 z-10 pointer-events-none" aria-hidden="true">
          <span className="block text-cove-gold text-2xl leading-none">✦</span>
          <span className="block text-cove-gold/70 text-lg leading-none mt-6 ml-2">✦</span>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full mx-4 md:mx-6 my-6 md:my-8 overflow-hidden shadow-[0_30px_60px_-25px_rgba(41,54,80,0.35)]"
          style={{ borderTopLeftRadius: "50% 40%", borderTopRightRadius: "50% 40%" }}
        >
          <img
            src={IMAGES.beachSunset}
            alt="A sunset cove with soft waves along the shore"
            className="w-full h-full object-cover min-h-[420px] md:min-h-full"
            fetchPriority="high"
          />
        </motion.div>
      </div>

      {/* Mobile-only book button below the arch */}
      <div className="md:hidden col-span-full flex justify-center pb-14 pt-2">
        <Link
          to="/contact"
          data-testid="hero-book-button-mobile"
          className="inline-flex items-center rounded-full bg-cove-slate px-10 py-4 text-sm tracking-[0.28em] uppercase text-cove-cream hover:bg-cove-nightblue transition-colors duration-300 shadow-[0_18px_40px_-15px_rgba(41,54,80,0.55)]"
          aria-label="Book Your Session"
        >
          Book Your Session
        </Link>
      </div>
    </div>
  </section>
);

const Intro = () => (
  <section className="px-6 md:px-12 py-24 md:py-36 grain bg-gradient-to-br from-cove-restorative via-cove-cream to-cove-blossom/50" data-testid="intro-section">
    <div className="mx-auto max-w-[1400px] grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-24 items-start">
      <Reveal>
        <p className="text-xs tracking-[0.25em] uppercase text-cove-rose">Welcome to the cove</p>
        <div className="mt-6 h-px w-16 bg-cove-rose/50" />
      </Reveal>
      <Reveal delay={0.1}>
        <p className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-cove-slate text-balance">
          <MaskedWords text="Gentle, intuitive care for your body, mind, and nervous system." />
        </p>
        <p className="mt-8 max-w-xl text-lg leading-relaxed text-cove-olive">
          Cranio Cove offers safe, trauma-informed, client-centered care. Together, we'll support
          your nervous system, release stored tension, and restore balance, one session at a time.
        </p>
      </Reveal>
    </div>
  </section>
);

const WhatIsCST = () => (
  <section className="relative bg-gradient-to-br from-cove-restorative via-cove-cream to-cove-blossom/40 py-20 md:py-28 px-6 md:px-12 overflow-hidden" data-testid="what-is-section">
    <FloatingBlob color="sage" size={340} className="-top-10 -right-20" delay={0.2} />
    <div className="relative mx-auto max-w-[1400px] grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <Reveal>
        <div
          className="relative mx-auto max-w-[520px] overflow-hidden shadow-[0_30px_60px_rgba(41,54,80,0.12)]"
          style={{ borderTopLeftRadius: "50% 40%", borderTopRightRadius: "50% 40%" }}
        >
          <img
            src={IMAGES.coveMoody}
            alt="A quiet marina at sunset, still water and dusk reflections"
            loading="lazy"
            className="w-full h-[380px] md:h-[560px] object-cover"
          />
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="text-xs tracking-[0.28em] uppercase text-cove-duskyrose mb-5">
          What is Craniosacral Therapy?
        </p>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.98] text-cove-slate">
          Healing begins when we <span className="italic text-cove-duskyrose">slow down</span>
        </h2>
        <p className="mt-8 text-lg leading-relaxed text-cove-olive max-w-xl">
          Craniosacral therapy is a gentle, hands-on approach that supports your body's natural
          ability to heal, helping you relax and recalibrate, restore balance, and experience deep,
          quiet healing.
        </p>
        <p className="mt-6 text-xs tracking-[0.4em] uppercase text-cove-rose">
          Align &nbsp;·&nbsp; Release &nbsp;·&nbsp; Heal
        </p>
      </Reveal>
    </div>
  </section>
);

const QuoteBand = () => null;

const Manifesto = () => (
  <section className="relative px-6 md:px-12 py-24 md:py-36 bg-cove-blush/30 grain overflow-hidden" data-testid="manifesto-section">
    <FloatingBlob color="sage" size={380} className="bottom-0 -right-24" delay={0.2} />
    <div className="relative mx-auto max-w-[1400px]">
      <Reveal>
        <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-6">The rhythm of a session</p>
        <h2 className="font-serif text-4xl md:text-6xl text-cove-slate max-w-2xl leading-[0.95]">
          Three <span className="italic text-cove-rose">gentle</span> movements
        </h2>
      </Reveal>
      <div className="mt-16 grid md:grid-cols-3 gap-x-12 gap-y-14">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.num} delay={i * 0.12}>
            <Pop lift={-6} className={i === 1 ? "md:mt-16" : i === 2 ? "md:mt-8" : ""}>
              <span className="font-serif text-6xl md:text-7xl text-cove-rose/70">{m.num}</span>
              <h3 className="mt-5 font-serif text-3xl text-cove-slate">{m.title}</h3>
              <p className="mt-4 text-cove-olive leading-relaxed">{m.body}</p>
            </Pop>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const MarqueeBand = () => (
  <section className="py-14 md:py-20 border-y border-cove-slate/10" data-testid="marquee-section">
    <Marquee items={MARQUEE} />
  </section>
);

const Reasons = () => (
  <section className="relative px-6 md:px-12 py-24 md:py-32 bg-gradient-to-b from-cove-blossom/50 via-cove-restorative to-cove-mutedblue/25 overflow-hidden" data-testid="reasons-section">
    <FloatingBlob color="blue" size={360} className="top-10 -left-20" delay={0.1} />
    <div className="relative mx-auto max-w-[1400px] grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-24">
      <Reveal>
        <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-6">Why people come</p>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-cove-slate">
          Reasons someone may <span className="italic text-cove-sage">seek</span> a session
        </h2>
        <p className="mt-6 text-cove-olive leading-relaxed max-w-md">
          People arrive at the cove for many different reasons. You are welcome whether you have a
          specific hope in mind or simply a wish for quiet, restorative time.
        </p>
        <div className="mt-8 overflow-hidden rounded-[2rem] hidden lg:block">
          <img src={IMAGES.coveHarbor} alt="A tranquil harbor at sunset with a hillside and quiet water" loading="lazy" className="w-full h-56 object-cover" />
        </div>
      </Reveal>
      <div className="grid sm:grid-cols-2 gap-4">
        {REASONS.map((r, i) => {
          const tints = [
            "bg-cove-blush/40",
            "bg-cove-mutedblue/25",
            "bg-cove-restorative",
            "bg-cove-blossom/60",
            "bg-cove-sage/30",
            "bg-cove-bluestone/20",
          ];
          return (
            <Reveal key={i} delay={i * 0.06}>
              <Pop className={`h-full rounded-[1.5rem] p-8 md:p-9 ${tints[i % tints.length]}`}>
                <Sparkles size={22} className="text-cove-gold" aria-hidden="true" />
                <p className="mt-3 text-cove-slate leading-relaxed">{r}</p>
              </Pop>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Difference = () => (
  <section className="relative px-6 md:px-12 py-24 md:py-32 text-cove-cream grain overflow-hidden" data-testid="difference-section">
    <img
      src={IMAGES.coveMoody}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-cove-slate/85 via-cove-nightblue/85 to-cove-olive/80" aria-hidden="true" />
    <div className="relative z-10 mx-auto max-w-[1400px]">
      <Reveal>
        <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-6">The approach</p>
        <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] max-w-3xl text-cove-cream">
          What makes Laura's approach <span className="italic text-cove-blush">different</span>
        </h2>
      </Reveal>
      <div className="mt-16 grid md:grid-cols-3 gap-10 lg:gap-16">
        {DIFFERENCE.map((d, i) => (
          <Reveal key={i} delay={i * 0.12}>
            <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 260, damping: 20 }}>
              <div className="h-px w-full bg-cove-cream/20 mb-6" />
              <Sparkles size={26} className="text-cove-gold" aria-hidden="true" />
              <h3 className="mt-3 font-serif text-3xl text-cove-cream">{d.title}</h3>
              <p className="mt-4 text-cove-blue leading-relaxed">{d.body}</p>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ExpectPreview = () => (
  <section className="px-6 md:px-12 py-24 md:py-32 bg-gradient-to-tr from-cove-restorative via-cove-blossom/40 to-cove-mutedblue/25" data-testid="expect-section">
    <div className="mx-auto max-w-[1400px]">
      <div className="grid lg:grid-cols-[1fr_0.9fr] gap-10 lg:gap-16 items-end">
        <Reveal>
          <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-6">What to expect</p>
          <h2 className="font-serif text-4xl md:text-6xl text-cove-slate max-w-2xl leading-[0.95]">
            A simple, <span className="italic text-cove-sage">unhurried</span> experience
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="overflow-hidden rounded-[2rem] shadow-[0_30px_60px_rgba(41,54,80,0.08)]">
            <img src={IMAGES.coveSunsetPurple} alt="A soft purple and gold sunset over calm water with silhouetted trees" loading="lazy" className="w-full h-56 md:h-64 object-cover" />
          </div>
        </Reveal>
      </div>
      <div className="mt-16 grid md:grid-cols-3 gap-6 lg:gap-8">
        {STEPS.map((s, i) => (
          <Reveal key={s.num} delay={i * 0.12}>
            <Pop className="h-full rounded-[1.75rem] bg-cove-blush/25 border border-cove-slate/10 p-8 md:p-9">
              <div className="flex items-baseline gap-4">
                <span className="font-serif text-5xl text-cove-rose/70">{s.num}</span>
                <h3 className="font-serif text-3xl text-cove-slate">{s.title}</h3>
              </div>
              <p className="mt-5 text-cove-olive leading-relaxed">{s.body}</p>
            </Pop>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-12">
          <CoveButton to="/what-to-expect" variant="secondary" testId="expect-more-button">
            See the full experience
          </CoveButton>
        </div>
      </Reveal>
    </div>
  </section>
);

const Practitioner = () => (
  <section className="px-6 md:px-12 py-24 md:py-32 bg-gradient-to-br from-cove-bluestone/25 via-cove-restorative to-cove-blossom/40" data-testid="practitioner-section">
    <div className="mx-auto max-w-[1400px] grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-24 items-center">
      <Reveal>
        <div
          className="mx-auto max-w-md overflow-hidden shadow-[0_30px_60px_rgba(41,54,80,0.1)]"
          style={{ borderTopLeftRadius: "50% 40%", borderTopRightRadius: "50% 40%" }}
        >
          <img
            src={IMAGES.lauraPortrait}
            alt="Laura McLelland, craniosacral practitioner, smiling warmly"
            loading="lazy"
            className="w-full h-[440px] md:h-[560px] object-cover object-[center_20%]"
          />
        </div>
      </Reveal>
      <div>
        <Reveal>
          <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-6">Your practitioner</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-cove-slate">
            Meet Laura McLelland
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 text-lg leading-relaxed text-cove-olive max-w-xl">
            Laura is a craniosacral practitioner who believes deeply in the quiet power of
            wellbeing and quality of life. Her work is gentle, attentive, and always led by your
            comfort and consent, offering a calm space where the body is free to rest, be present
            and reset.
          </p>
          <div className="mt-8">
            <CoveButton to="/about" variant="secondary" testId="practitioner-about-button">
              About Laura
            </CoveButton>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section className="relative px-6 md:px-12 py-24 md:py-36 bg-gradient-to-b from-cove-blossom/40 via-cove-restorative to-cove-sage/25 overflow-hidden" data-testid="testimonials-section">
    <FloatingBlob color="rose" size={340} className="-top-16 right-10" delay={0.3} />
    <div className="relative mx-auto max-w-[1400px]">
      <Reveal>
        <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-3 text-center">In their words</p>
        <h2 className="font-serif text-4xl md:text-5xl text-cove-slate text-center">Quiet reflections</h2>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-14">
        {TESTIMONIALS.map((t, i) => {
          const tints = ["bg-cove-cream", "bg-cove-blossom/50", "bg-cove-mutedblue/20"];
          return (
            <Reveal key={i} delay={i * 0.12}>
              <Pop className={`h-full rounded-[2rem] p-8 md:p-10 border border-cove-slate/10 ${tints[i % tints.length]}`}>
                <figure className="h-full flex flex-col">
                  <span className="font-serif text-6xl leading-none text-cove-rose/50">“</span>
                  <blockquote className="-mt-4 font-serif text-2xl md:text-[1.6rem] leading-[1.3] text-cove-slate italic">
                    {t.quote}
                  </blockquote>
                  <figcaption className="mt-6 text-sm text-cove-taupe">
                    <span className="block text-cove-olive">{t.name}</span>
                    {t.detail}
                  </figcaption>
                </figure>
              </Pop>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const FaqPreview = () => (
  <section className="px-6 md:px-12 py-20 md:py-28 bg-gradient-to-br from-cove-restorative via-cove-blossom/40 to-cove-mutedblue/30" data-testid="faq-preview-section">
    <div className="mx-auto max-w-[1400px] grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-24">
      <Reveal>
        <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-6">Common questions</p>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-cove-slate">
          A little more clarity, before you arrive
        </h2>
        <div className="mt-8">
          <CoveButton to="/faq" variant="secondary" testId="faq-preview-button">
            Read all questions
          </CoveButton>
        </div>
      </Reveal>
      <div>
        {FAQS.slice(0, 4).map((f, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="py-6 border-b border-cove-slate/15">
              <h3 className="font-serif text-2xl text-cove-slate">{f.q}</h3>
              <p className="mt-3 text-cove-olive leading-relaxed">{f.a}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FinalCta = () => (
  <section className="px-6 md:px-12 py-28 md:py-40 relative overflow-hidden grain" data-testid="final-cta-section">
    <div className="absolute inset-0 -z-10">
      <img src={IMAGES.coveBeach} alt="" aria-hidden="true" loading="lazy" className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-cove-cream/85" />
    </div>
    <div className="mx-auto max-w-[1400px] text-center">
      <Reveal>
        <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] text-cove-slate text-balance">
          Welcome to the Cove
          <span className="block italic text-cove-duskyrose">Your Safe Harbor</span>
        </h2>
        <p className="mt-8 max-w-xl mx-auto text-lg text-cove-olive leading-relaxed">
          When you're ready, book a gentle first session or reach out with any questions. You'll
          hear back from Laura personally.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <CoveButton calendly variant="primary" testId="final-book-button">
            Book a Session <ArrowRight size={16} />
          </CoveButton>
          <CoveButton to="/services" variant="secondary" testId="final-services-button">
            View Services & Pricing
          </CoveButton>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-20 grid sm:grid-cols-3 gap-8 text-sm text-cove-olive border-t border-cove-slate/15 pt-12 max-w-3xl mx-auto">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-cove-taupe mb-2">Email</p>
            <a href={`mailto:${SITE.email}`} className="hover:text-cove-slate transition-colors">{SITE.email}</a>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-cove-taupe mb-2">Phone</p>
            <p>{SITE.phone}</p>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-cove-taupe mb-2">Service area</p>
            <p>{SITE.serviceArea}</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Private Craniosacral Practice"
        description="Cranio Cove is a private craniosacral practice with Laura McLelland, offering gentle, individualized sessions in a peaceful, supportive environment. Book a session today."
        path="/"
        schema={localBusinessSchema}
      />
      <Hero />
      <Intro />
      <WhatIsCST />
      <QuoteBand />
      <Reasons />
      <Difference />
      <ExpectPreview />
      <Practitioner />
      <FaqPreview />
      <FinalCta />
    </>
  );
}
